import {
	LoadingOutlined,
} from "@ant-design/icons";
import { Spin, Checkbox } from "antd";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const CompareSalvage = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [compareCars, setCompareCars] = useState([]);
	const [checkGenerate, setCheckedGenerate] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isDisabled, setIsDisabled] = useState(true);

	useEffect(() => {
		if (location?.state?.CompareCars) {
			if (location?.state?.CompareCars.length === 4) {
				const data = location?.state?.CompareCars;
				localStorage.setItem("compareSalvage", JSON.stringify(data));
				setCompareCars(data);
			} else {
				const data = [...location?.state?.CompareCars, { emptyData: true }];
				setCompareCars(data);
			}
		}
	}, [location]);

	useEffect(() => {
		if (checkGenerate.length) {
			setIsDisabled(false);
		} else {
			setIsDisabled(true);
		}
	}, [checkGenerate]);

	const handleRemove = (id) => {
		const filtered = compareCars.filter((item) => {
			return id !== item?.vin;
		});
		setCompareCars(filtered);
		const filterGenerate = filtered
			.filter((item) => {
				return checkGenerate.includes(item?.vin);
			})
			.map((item) => item?.vin);
		setCheckedGenerate(filterGenerate);
		const filterEmptyData = filtered.filter((item) => {
			return !item.emptyData;
		});
		localStorage.setItem("compareSalvage", JSON.stringify(filterEmptyData));
		if (!filtered[filtered.length - 1].emptyData) {
			setCompareCars([...filtered, { emptyData: true }]);
		} else {
			if (filtered.length === 2) {
				navigate("/find_salvageCar", {
					state: {
						filterInput: location?.state?.filters,
						formInputValues: location?.state?.inputFormValues,
					},
				});
			}
		}
	};

	const handleCheckChange = (e, car) => {
		const res = car?.vin;
		if (e.target.checked) {
			setCheckedGenerate([...checkGenerate, res]);
		} else {
			const filtered = checkGenerate.filter((item) => {
				return item !== res;
			});
			setCheckedGenerate(filtered);
		}
	};

	const handleAddToReport = async () => {
		const selectedCars = compareCars.filter((item) =>
			checkGenerate.includes(item?.vin),
		);

		try {
			setLoading(true);

			const updatedCars = selectedCars.map((item) => {
				if (item.bid === null) {
					return {
						...item,
						bid: null,
					};
				}
				return item;
			});

			localStorage.setItem("salvageCars", JSON.stringify(updatedCars));
			navigate("/generatePage");
		} catch (e) {
			console.error(e);
		} finally {
			setLoading(false);
		}
	};

	const getImageUrls = (res) => {
		try {
			if (res?.images?.[0]) {
				const parsed = JSON.parse(res.images[0]);
				if (Array.isArray(parsed)) {
					return parsed.filter((_, i) => i < 6);
				}
			}
		} catch {
			// ignore parse errors
		}
		return [];
	};

	const getSourceName = (domainId) => {
		if (domainId === 1) return "IAA";
		if (domainId === 3) return "Copart";
		return "-";
	};

	const getAddress = (res) => {
		const parts = [res?.city, res?.state].filter(Boolean);
		return parts.length > 0 ? parts.join(", ") : "-";
	};

	const DetailRow = ({ label, value }) => (
		<div className="flex justify-between items-center py-2.5 border-b border-slate-700/50 last:border-b-0">
			<span className="text-slate-400 text-sm">{label}</span>
			<span className="text-white font-medium text-sm text-right max-w-[60%] truncate">
				{value || "-"}
			</span>
		</div>
	);

	return (
		<div className="compare-cars-page">
			<Spin
				spinning={loading}
				indicator={
					<LoadingOutlined
						style={{ fontSize: 48, color: "#38bdf8" }}
						spin
					/>
				}
			>
				{/* Hero Header */}
				<div className="pt-28 pb-8 px-4 text-center">
					<h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
						Compare Salvage Vehicles
					</h1>
					<p className="text-slate-400 text-base max-w-xl mx-auto">
						Review and select salvage vehicles to include in your conditions report
					</p>
					{compareCars.filter((c) => !c.emptyData).length > 0 && (
						<div className="mt-4 inline-flex items-center gap-2 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-full px-4 py-1.5">
							<span className="text-slate-400 text-sm">
								{compareCars.filter((c) => !c.emptyData).length} of 4 vehicles
							</span>
							{checkGenerate.length > 0 && (
								<>
									<span className="text-slate-600">|</span>
									<span className="text-cyan-400 text-sm font-medium">
										{checkGenerate.length} selected
									</span>
								</>
							)}
						</div>
					)}
				</div>

				{/* Cards Grid */}
				<div className="max-w-7xl mx-auto px-4 pb-8">
					<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
						{compareCars.map((res, i) => (
							<div key={res?.vin || `empty-${i}`}>
								{!compareCars[i].emptyData ? (
									<div className="compare-vehicle-card group">
										{/* Image Section */}
										<div className="relative overflow-hidden">
											{getImageUrls(res).length > 0 ? (
												<img
													src={getImageUrls(res)[0]}
													alt={`${res?.year} ${res?.manufacturer} ${res?.model}`}
													className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
												/>
											) : (
												<div className="w-full h-48 bg-slate-800 flex items-center justify-center">
													<svg className="w-16 h-16 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<rect x="2" y="6" width="20" height="12" rx="2" strokeWidth="1.5" />
														<circle cx="7" cy="18" r="2" strokeWidth="1.5" />
														<circle cx="17" cy="18" r="2" strokeWidth="1.5" />
													</svg>
												</div>
											)}
											{/* Gradient overlay */}
											<div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />

											{/* Remove button */}
											<button
												type="button"
												onClick={() => handleRemove(res?.vin)}
												className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500/90 hover:bg-red-500 text-white flex items-center justify-center transition-all shadow-lg hover:scale-110 backdrop-blur-sm"
											>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
													<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
												</svg>
											</button>

											{/* Bid badge */}
											<div className="absolute bottom-3 left-3 bg-cyan-500/90 backdrop-blur-sm text-white font-bold px-3 py-1 rounded-lg text-sm shadow-lg">
												{res?.bid
													? `$${res.bid}`
													: "No Bid"}
											</div>
										</div>

										{/* Vehicle Name */}
										<div className="px-4 pt-4 pb-3 border-b border-slate-700/50">
											<h3 className="text-white font-semibold text-base leading-tight">
												{res?.title || `${res?.year} ${res?.manufacturer} ${res?.model}`}
											</h3>
											<span className="text-cyan-400 text-xs font-medium mt-0.5 inline-block">
												{getSourceName(res?.domain_id)}
											</span>
										</div>

										{/* Details */}
										<div className="px-4 py-2 flex-1">
											<DetailRow label="VIN" value={res?.vin?.toUpperCase()} />
											<DetailRow label="Year" value={res?.year} />
											<DetailRow label="Make" value={res?.manufacturer} />
											<DetailRow label="Model" value={res?.model} />
											<DetailRow label="Mileage" value={res?.mileage} />
											<DetailRow label="Source" value={getSourceName(res?.domain_id)} />
											<DetailRow label="Dealer" value={res?.seller_name} />
											<DetailRow label="Location" value={getAddress(res)} />
											<DetailRow
												label="Final Bid"
												value={
													res?.bid
														? `$${res.bid}`
														: "No Auction in last 6 months"
												}
											/>
										</div>

										{/* Checkbox */}
										<div className="px-4 py-3 border-t border-slate-700/50">
											<Checkbox
												checked={checkGenerate.includes(res?.vin)}
												className="compare-checkbox"
												onChange={(e) => handleCheckChange(e, res)}
											>
												<span className="text-slate-300 text-sm">Add to Report</span>
											</Checkbox>
										</div>
									</div>
								) : (
									/* Add More Card */
									<div
										className="compare-vehicle-card flex items-center justify-center min-h-[400px] cursor-pointer group"
										onClick={() =>
											navigate("/find_salvageCar", {
												state: {
													filterInput: location?.state?.filters,
													formInputValues:
														location?.state?.inputFormValues,
												},
											})
										}
									>
										<div className="text-center p-6">
											<div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-dashed border-slate-600 group-hover:border-cyan-400 flex items-center justify-center transition-colors duration-300">
												<svg className="w-8 h-8 text-slate-500 group-hover:text-cyan-400 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
													<path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
												</svg>
											</div>
											<h3 className="text-white font-semibold mb-1">Add Vehicle</h3>
											<p className="text-slate-400 text-sm">Browse and add more salvage cars to compare</p>
										</div>
									</div>
								)}
							</div>
						))}
					</div>
				</div>

				{/* Bottom CTA */}
				<div className="flex justify-center pb-16 px-4">
					<button
						className={`px-10 py-4 rounded-xl font-semibold text-base transition-all duration-300 ${
							isDisabled
								? "bg-slate-700/50 text-slate-500 cursor-not-allowed"
								: "bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:-translate-y-0.5"
						}`}
						disabled={isDisabled}
						onClick={handleAddToReport}
					>
						Add to Conditions Report
					</button>
				</div>
			</Spin>
		</div>
	);
};

export default CompareSalvage;
